import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useLocation } from "react-router-dom";
import axios from "axios";
import SubTitle from "../common/SubTitle";
import DynamicTable from "../common/DynamicTable";
import SearchFilter from "../common/SearchFilter";
import config from "../../config.js";

const STOP_BASE_URL = `${config.baseURL}/stop`;
const STOP_SCHEDULE_BASE_URL = `${config.baseURL}/schedule/stop`;

const headers = [
  { key: "vehicles", value: "vehicles" },
  { key: "arrivalTime", value: "arrivalTime" },
  { key: "departureTime", value: "departureTime" },
  { key: "repitation", value: "repitation" },
  { key: "routes", value: "routes" },
];

const getAuthRequestConfig = (params = {}) => {
  const token = localStorage.getItem("token");

  return {
    params,
    withCredentials: true,
    ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {}),
  };
};

const getStopScheduleForSelectedStop = (schedule, selectedStopId) => {
  const stopSchedules = Array.isArray(schedule?.stopSchedules)
    ? schedule.stopSchedules
    : [];

  return (
    stopSchedules.find(
      (stopSchedule) => Number(stopSchedule?.stopId) === Number(selectedStopId),
    ) || null
  );
};

const getVehicleNames = (schedule) => {
  const summaryNames = Array.isArray(schedule?.vehicleScheduleSummary)
    ? schedule.vehicleScheduleSummary
        .map(
          (vehicleSummary) =>
            vehicleSummary?.vehicleName || vehicleSummary?.vehicleNumber || "",
        )
        .filter(Boolean)
    : [];

  if (summaryNames.length > 0) {
    return summaryNames.join(", ");
  }

  const assignedVehicleNames = Array.isArray(schedule?.vehicleSchedule)
    ? schedule.vehicleSchedule
        .map(
          (vehicleSchedule) =>
            vehicleSchedule?.vehicle?.name ||
            vehicleSchedule?.vehicle?.vehicleNumber ||
            vehicleSchedule?.vehicle?.VehicleNumber ||
            "",
        )
        .filter(Boolean)
    : [];

  return assignedVehicleNames.join(", ");
};

const formatTime = (timeString) => {
  if (!timeString) {
    return "-";
  }

  let parsedDate;

  if (/^\d{2}:\d{2}(:\d{2})?$/.test(timeString)) {
    const [hours, minutes] = timeString.split(":").map(Number);
    parsedDate = new Date();
    parsedDate.setHours(hours, minutes, 0, 0);
  } else {
    parsedDate = new Date(timeString);
  }

  if (Number.isNaN(parsedDate.getTime())) {
    return "-";
  }

  return parsedDate.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

export default function StopSchedule() {
  const [stopData, setStopData] = useState({});
  const [schedules, setSchedules] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({});

  const location = useLocation();
  const { id } = location.state || {};

  useEffect(() => {
    const fetchStopData = async () => {
      if (!id) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      const [stopResponse, stopSchedulesResponse] = await Promise.allSettled([
        axios.get(`${STOP_BASE_URL}/id`, getAuthRequestConfig({ id })),
        axios.get(STOP_SCHEDULE_BASE_URL, getAuthRequestConfig({ id })),
      ]);

      if (stopResponse.status === "fulfilled") {
        setStopData(stopResponse.value.data || {});
      } else {
        console.error("Error fetching stop data:", stopResponse.reason);
      }

      if (stopSchedulesResponse.status === "fulfilled") {
        const stopSchedules = Array.isArray(stopSchedulesResponse.value.data)
          ? stopSchedulesResponse.value.data
          : [];

        const normalizedSchedules = stopSchedules.map((schedule) => {
          const selectedStopSchedule = getStopScheduleForSelectedStop(
            schedule,
            id,
          );
          const vehicleNames = getVehicleNames(schedule);

          return {
            id: schedule?.id,
            routes: schedule?.route?.name || "",
            routeName: schedule?.route?.name || "",
            vehicles: vehicleNames,
            vehicleNames,
            arrivalTime: selectedStopSchedule?.arrivalTime || "",
            departureTime: selectedStopSchedule?.departureTime || "",
            repitation: schedule?.repitation || "",
          };
        });

        setSchedules(normalizedSchedules);
      } else {
        console.error(
          "Error fetching stop schedules:",
          stopSchedulesResponse.reason,
        );
        setSchedules([]);
        setError(stopSchedulesResponse.reason);
      }

      if (
        stopResponse.status === "rejected" &&
        stopSchedulesResponse.status === "rejected"
      ) {
        setError(stopSchedulesResponse.reason || stopResponse.reason);
      }

      setIsLoading(false);
    };

    fetchStopData();
  }, [id]);

  const filterConfig = useMemo(
    () => [
      {
        id: "repitation",
        label: "Repetition",
        options: [
          { value: "DAILY", label: "Daily" },
          { value: "WEEKLY", label: "Weekly" },
          { value: "MONTHLY", label: "Monthly" },
          { value: "ONETIME", label: "One time" },
        ],
      },
      {
        id: "route",
        label: "Routes",
        options: [...new Set(schedules.map((schedule) => schedule.routeName))]
          .filter(Boolean)
          .map((route) => ({
            value: route,
            label: route,
          })),
      },
      {
        id: "vehicle",
        label: "Vehicles",
        options: [
          ...new Set(
            schedules.flatMap((schedule) =>
              schedule.vehicleNames
                ? schedule.vehicleNames.split(", ").filter(Boolean)
                : [],
            ),
          ),
        ].map((vehicle) => ({ value: vehicle, label: vehicle })),
      },
    ],
    [schedules],
  );

  const filteredSchedules = useMemo(() => {
    return schedules.filter((schedule) => {
      const repitationMatch =
        !filters.repitation || schedule.repitation === filters.repitation;
      const routeMatch = !filters.route || schedule.routeName === filters.route;
      const vehicleMatch =
        !filters.vehicle || schedule.vehicleNames?.includes(filters.vehicle);

      if (!repitationMatch || !routeMatch || !vehicleMatch) {
        return false;
      }

      if (!searchQuery) {
        return true;
      }

      const query = searchQuery.toLowerCase();

      return (
        schedule.vehicleNames?.toLowerCase().includes(query) ||
        schedule.routeName?.toLowerCase().includes(query) ||
        schedule.repitation?.toLowerCase().includes(query)
      );
    });
  }, [filters, schedules, searchQuery]);

  const tableData = filteredSchedules.map((schedule) => ({
    id: schedule.id,
    routes: schedule.routeName || "-",
    vehicles: schedule.vehicleNames || "-",
    arrivalTime: formatTime(schedule.arrivalTime),
    departureTime: formatTime(schedule.departureTime),
    repitation: schedule.repitation || "-",
  }));

  const handleSearchChange = (value) => {
    setSearchQuery(value);
  };

  const handleFilterChange = (filterValues) => {
    setFilters(filterValues);
  };

  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;

  useEffect(() => {
    if (error) {
      console.error("Stop schedule page error:", error);
    }
  }, [error]);

  return (
    <>
      <SubTitle
        title="Stops Schedules"
        path={`${orgName || "organization"} > Stops > ${stopData.name || "Stops Schedules"} > View Schedules`}
        breadcrumbHandlers={{
          [stopData.name]: stopData.name ? { to: `/stop/${id}` } : null,
        }}
      />
      <Box
        sx={{
          display: "flex",
          height: "60px",
          justifyContent: "space-between",
          padding: "12px 16px",
          margin: "0px 40px",
          backgroundColor: "#FFFFFF",
          boxShadow: "0px 2px 5px 0px #00000040",
        }}
      >
        <Box
          sx={{
            fontWeight: 500,
            fontFamily: "Roboto",
            fontSize: "20px",
            lineHeight: "100%",
            letterSpacing: "0%",
            display: "flex",
            alignItems: "center",
          }}
        >
          <span
            style={{
              color: "#414958",
              padding: "2px 6px",
              borderRadius: "2px",
            }}
          >
            Stop:
          </span>
          <span
            style={{
              fontWeight: 600,
              fontFamily: "Roboto",
              fontSize: "20px",
              lineHeight: "100%",
              letterSpacing: "0%",
              color: "#183059",
              padding: "2px 6px",
              borderRadius: "2px",
              marginLeft: "4px",
            }}
          >
            {stopData.name || "-"}
          </span>
        </Box>
        <Box
          sx={{
            fontWeight: 500,
            fontFamily: "Roboto",
            fontSize: "20px",
            lineHeight: "100%",
            display: "flex",
            alignItems: "center",
          }}
        >
          <span
            style={{
              color: "#414958",
              padding: "2px 6px",
              borderRadius: "2px",
            }}
          >
            Stop ID:
          </span>
          <span
            style={{
              fontWeight: 600,
              fontFamily: "Roboto",
              fontSize: "20px",
              lineHeight: "100%",
              color: "#183059",
              padding: "2px 6px",
              borderRadius: "2px",
              marginLeft: "4px",
            }}
          >
            {stopData.stopId || "-"}
          </span>
        </Box>
      </Box>
      <Box
        sx={{
          margin: "20px 40px",
          borderRadius: "10px",
          padding: "24px",
          border: "1px solid #A5DCF3",
          boxShadow: "-2px 1px 5px 0px #00000040",
        }}
      >
        <SearchFilter
          filters={filterConfig}
          searchPlaceholder="Search..."
          onSearchChange={handleSearchChange}
          onFilterChange={handleFilterChange}
        />

        <DynamicTable headers={headers} data={tableData} loading={isLoading} />
      </Box>
      <Box sx={{ height: "40vh" }}></Box>
    </>
  );
}
